// components/ProductSamplePage.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { loadSampleRequests, deleteSampleRequest, updateSampleRequest, invokeSampleShippedEmail, shippedNotifyWarning } from "../services/posService";
import NewRequestModal from "./NewRequestModal";
import ShippingEditModal from "./ShippingEditModal";
import SampleApprovalModal from "./SampleApprovalModal";
import {
  APPROVAL,
  approvalBadge,
  approvalStatusOf,
  isAwaitingApproval,
  isFromEmbed,
  isShipBlocked,
  shipBlockedMessage,
  shipStatusOptions,
} from "../utils/approval.js";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import "./ProductSamplePage.css";

const STATUS_CONFIG = {
  "In Process":  { label: "In Process",  cls: "status-in-process" },
  "ON HOLD":     { label: "On Hold",     cls: "status-on-hold" },
  "Not Shipped": { label: "Not Shipped", cls: "status-not-shipped" },
  "Shipped":     { label: "Shipped",     cls: "status-shipped" },
};

const ALL_STATUSES = ["In Process", "ON HOLD", "Not Shipped", "Shipped"];

function fmt(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function trackingUrl(carrier, tracking) {
  if (!tracking || tracking.length < 5) return null;
  if (/fedex/i.test(carrier)) return `https://www.fedex.com/fedextrack/?trknbr=${tracking}`;
  if (/ups/i.test(carrier))   return `https://www.ups.com/track?tracknum=${tracking}`;
  return null;
}

export default function ProductSamplePage({ user, userPermissions, onHome }) {
  const canDeleteRequest = userPermissions?.has("delete_sample_requests") ?? false;
  const canEditShipping  = canDeleteRequest;
  const canEdit          = canDeleteRequest;

  const [sampleRequests,  setSampleRequests]  = useState([]);
  const [showNewModal,    setShowNewModal]    = useState(false);
  const [successToast,    setSuccessToast]    = useState(false);
  // Item 204: a shipped notification that never left must be visible here, not
  // only in a function log. Sticky (no auto-dismiss) — it needs acting on.
  const [notifyWarning,   setNotifyWarning]   = useState(null);
  const [shippingEditRow, setShippingEditRow] = useState(null); // row being edited
  // Item 124 / migration 0097: the row open in the approval review modal.
  const [reviewRow,       setReviewRow]       = useState(null);
  const [loadingData,     setLoadingData]     = useState(true);
  const [search,         setSearch]         = useState("");
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [sectionFilter,  setSectionFilter]  = useState("all"); // all | new | shipped
  // all | pending | approved | rejected — a SECOND dimension, deliberately not
  // folded into statusFilter. "Needs approval" and "Not Shipped" are different
  // questions and a reviewer asks them separately.
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [expandedId,     setExpandedId]     = useState(null);
  const [sortKey,        setSortKey]        = useState(null);
  const [sortDir,        setSortDir]        = useState("asc");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting,        setDeleting]        = useState(false);
  const [editCell,        setEditCell]        = useState(null); // { rowId, field }
  const [editValue,       setEditValue]       = useState("");

  useEffect(() => {
    loadSampleRequests()
      .then(setSampleRequests)
      .catch(err => console.error("Failed to load sample requests:", err))
      .finally(() => setLoadingData(false));
  }, []);

  // Item 204. pos-shipped-notify answers 200 { sent:false, reason } on every
  // failure so a shipping update is never blocked by a mail problem — which
  // means the ONLY place that can tell the user is here.
  const showNotifyWarning = (result) => setNotifyWarning(shippedNotifyWarning(result));

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await deleteSampleRequest(id);
      setSampleRequests(prev => prev.filter(r => r.id !== id));
      setExpandedId(null);
      setConfirmDeleteId(null);
    } catch (err) {
      // Say so. This used to console.error and stop, so a delete refused by RLS
      // left the row sitting on screen with no message — indistinguishable from
      // a UI that had simply not repainted, and the user's next move is to press
      // it again. Reuses the existing warning banner rather than inventing a
      // second error surface.
      console.error("Delete failed:", err);
      setNotifyWarning(
        err?.message ||
          "Could not delete that request. It is still there — reload and try again.",
      );
      setConfirmDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  const startEdit  = (rowId, field, value, e) => { if (e) e.stopPropagation(); setEditCell({ rowId, field }); setEditValue(value ?? ""); };
  const cancelEdit = () => { setEditCell(null); setEditValue(""); };
  const commitEdit = async () => {
    if (!editCell) return;
    const { rowId, field } = editCell;
    const val = field === "quantity" ? (parseInt(editValue, 10) || null) : (editValue.trim() || null);
    const prevRow = sampleRequests.find(r => r.id === rowId);
    setEditCell(null);
    setEditValue("");
    // The gate, before the write. `Shipped` is already absent from the options
    // for a blocked row, so this only catches a stale grid — but a stale grid is
    // exactly how someone used to reach 0097's trigger and get "Could not save".
    if (field === "status" && val === "Shipped" && isShipBlocked(prevRow)) {
      setNotifyWarning(shipBlockedMessage(prevRow));
      return;
    }
    try {
      await updateSampleRequest(rowId, { [field]: val });
      const updatedRow = { ...prevRow, [field]: val };
      setSampleRequests(prev => prev.map(r => r.id === rowId ? updatedRow : r));
      // Fire shipped notification when status transitions to Shipped
      if (field === "status" && val === "Shipped" && prevRow?.status !== "Shipped") {
        invokeSampleShippedEmail(updatedRow, {}, user?.mail)
          .then(showNotifyWarning)
          .catch(err => { console.error(err); setNotifyWarning("Marked Shipped, but the notification email was NOT sent."); });
      }
    } catch (err) {
      // Used to be console-only, which is how an approval refusal reached the
      // user as nothing at all. Every inline-edit failure now says something.
      console.error("Update failed:", err);
      setNotifyWarning(err.message || "Could not save that change.");
    }
  };

  // Ruling from the review modal. Replace the row in place so the badge, the
  // counts and the Shipped option all update without a reload.
  const handleRuled = (updated) => {
    setSampleRequests(prev => prev.map(r => (r.id === updated.id ? updated : r)));
  };

  const isEdit = (rowId, field) => editCell?.rowId === rowId && editCell?.field === field;

  const spInput = (rowId, field, type = "text") => (
    <input
      className="sp-inline-input"
      type={type}
      value={editValue}
      autoFocus
      onChange={e => setEditValue(e.target.value)}
      onBlur={commitEdit}
      onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }}
      onClick={e => e.stopPropagation()}
    />
  );

  const spSelect = (rowId, field, options) => (
    <select
      className="sp-inline-input sp-inline-select"
      value={editValue}
      autoFocus
      onChange={e => setEditValue(e.target.value)}
      onBlur={commitEdit}
      onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
      onClick={e => e.stopPropagation()}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const spCell = (rowId, field, display, rawValue, type = "text", options = null) => {
    if (isEdit(rowId, field)) return options ? spSelect(rowId, field, options) : spInput(rowId, field, type);
    return canEdit
      ? <span className="sp-editable" onClick={e => startEdit(rowId, field, rawValue, e)}>{display}</span>
      : display;
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return (
      <svg className="sp-sort-icon sp-sort-idle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 5 19 12"/>
      </svg>
    );
    return sortDir === "asc" ? (
      <svg className="sp-sort-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 5 19 12"/>
      </svg>
    ) : (
      <svg className="sp-sort-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 19 19 12"/>
      </svg>
    );
  };

  // ─── Column definitions with resizable widths ─────────────────────────────
  const COLS = [
    { key: "__details",         label: "",                  defaultW: 80  },
    { key: "status",            label: "Status",            defaultW: 120 },
    { key: "shipToCompany",     label: "Company",           defaultW: 170 },
    { key: "contactName",       label: "Contact",           defaultW: 160 },
    { key: "state",             label: "State",             defaultW: 120 },
    { key: "product",           label: "Product(s)",        defaultW: 260 },
    { key: "orderDate",         label: "Order Date",        defaultW: 115 },
    { key: "requestedShipDate", label: "Req. Ship",         defaultW: 115 },
    { key: "submitter",         label: "Submitted By",      defaultW: 150 },
    { key: "shipDate",          label: "Ship Date",         defaultW: 115 },
    { key: "carrier",           label: "Carrier / Tracking",defaultW: 210 },
    { key: null,                label: "",                  defaultW: canEditShipping ? 160 : 40 },
  ];
  const [colWidths, setColWidths] = useState(() => Object.fromEntries(COLS.map(c => [c.key ?? "__notes", c.defaultW])));
  const resizeRef = useRef(null); // { key, startX, startW }

  const startResize = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key ?? "__notes"];
    const onMove = (me) => {
      const newW = Math.max(40, startW + me.clientX - startX);
      setColWidths(prev => ({ ...prev, [key ?? "__notes"]: newW }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  // Summary counts
  const counts = useMemo(() => {
    const c = { total: 0, "In Process": 0, "ON HOLD": 0, "Not Shipped": 0, "Shipped": 0, pending: 0 };
    sampleRequests.forEach(r => {
      c.total++;
      if (c[r.status] !== undefined) c[r.status]++;
      if (isAwaitingApproval(r)) c.pending++;
    });
    return c;
  }, [sampleRequests]);

  // Filtered + sorted rows
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const rows = sampleRequests.filter(r => {
      if (sectionFilter !== "all" && r.section !== sectionFilter) return false;
      if (statusFilter  !== "all" && r.status !== statusFilter)   return false;
      if (approvalFilter !== "all" && approvalStatusOf(r) !== approvalFilter) return false;
      if (q) {
        const hay = [r.shipToCompany, r.contactName, r.state, r.product, r.submitter, r.tracking].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [sampleRequests, search, statusFilter, sectionFilter, approvalFilter, sortKey, sortDir]);

  // "Show me the queue" — one click from the banner and from the summary card.
  const showReviewQueue = () => {
    setApprovalFilter(APPROVAL.PENDING);
    setStatusFilter("all");
    setSectionFilter("all");
    setSearch("");
  };

  if (loadingData) {
    return (
      <div className="sp-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
        <div style={{ textAlign: "center", color: "var(--muted)" }}>
          <div className="loading-spinner" style={{ margin: "0 auto 12px" }} />
          Loading sample requests…
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="sp-page">

      {/* ── Top bar ── */}
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <HomeButton onClick={onHome} />
          <h2 className="sp-title">Product Sample Requests</h2>
        </div>
        <div className="sp-toolbar-right">
          <button className="sp-new-btn" onClick={() => setShowNewModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Request
          </button>
        </div>
      </div>

      {/* ── Review queue banner (migration 0097 / item 124) ──
          A request from the public form ships to an address an anonymous caller
          typed. Nothing about it is actionable until a human has looked, so the
          count is the first thing on the screen — not a badge to notice. */}
      {counts.pending > 0 && (
        <div className="sp-review-banner" role="status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" />
          </svg>
          <span className="sp-review-banner-text">
            <strong>
              {counts.pending === 1
                ? "1 request is waiting on you."
                : `${counts.pending} requests are waiting on you.`}
            </strong>{" "}
            {counts.pending === 1
              ? "It came in through your public sample form with a ship-to address the requester typed, and cannot be shipped until you approve it."
              : "They came in through your public sample form with ship-to addresses the requesters typed, and cannot be shipped until you approve them."}
          </span>
          <button
            type="button"
            className="sp-review-banner-btn"
            onClick={showReviewQueue}
            disabled={approvalFilter === APPROVAL.PENDING}
          >
            {approvalFilter === APPROVAL.PENDING ? "Showing the queue" : "Review them"}
          </button>
        </div>
      )}

      {/* ── Summary cards ── */}
      <div className="sp-summary">
        {[
          { label: "Total Orders",  value: counts.total,          cls: "",             filterKey: "all"          },
          { label: "In Process",    value: counts["In Process"],  cls: "sp-card-blue", filterKey: "In Process"   },
          { label: "On Hold",       value: counts["ON HOLD"],     cls: "sp-card-amber",filterKey: "ON HOLD"      },
          { label: "Not Shipped",   value: counts["Not Shipped"], cls: "sp-card-gray", filterKey: "Not Shipped"  },
          { label: "Shipped",       value: counts["Shipped"],     cls: "sp-card-green",filterKey: "Shipped"      },
        ].map(c => (
          <button
            key={c.label}
            className={`sp-card ${c.cls}${statusFilter === c.filterKey && approvalFilter === "all" ? " sp-card-active" : ""}`}
            onClick={() => { setStatusFilter(c.filterKey); setApprovalFilter("all"); }}
          >
            <div className="sp-card-value">{c.value}</div>
            <div className="sp-card-label">{c.label}</div>
          </button>
        ))}
        {/* Filters on the approval dimension, not the fulfilment one, so it is
            its own card rather than a sixth status. Only shown once the tenant
            actually has a public form producing rows — a brand that never
            enabled the embed sees the screen exactly as it is today. */}
        {counts.pending > 0 && (
          <button
            className={`sp-card sp-card-review${approvalFilter === APPROVAL.PENDING ? " sp-card-active" : ""}`}
            onClick={() =>
              approvalFilter === APPROVAL.PENDING ? setApprovalFilter("all") : showReviewQueue()
            }
          >
            <div className="sp-card-value">{counts.pending}</div>
            <div className="sp-card-label">Needs Approval</div>
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="sp-filters">
        {/* Section tabs */}
        <div className="sp-tabs">
          {[["all","All"],["new","New Orders"],["shipped","Shipped"]].map(([val, label]) => (
            <button
              key={val}
              className={`sp-tab${sectionFilter === val ? " sp-tab-active" : ""}`}
              onClick={() => setSectionFilter(val)}
            >{label}</button>
          ))}
        </div>

        {/* Active review filter. Rendered from `approvalFilter` alone and NOT
            from the pending count — approving the last request in the queue
            takes the banner away, and without this the reviewer would be left
            looking at an empty grid with nothing on screen explaining why. */}
        {approvalFilter !== "all" && (
          <button
            type="button"
            className="sp-appr-filter-chip"
            onClick={() => setApprovalFilter("all")}
            title="Clear the review filter"
          >
            {approvalFilter === APPROVAL.PENDING ? "Needs approval" :
             approvalFilter === APPROVAL.APPROVED ? "Approved" : "Rejected"} only
            <span aria-hidden="true">×</span>
          </button>
        )}

        {/* Status filter pills */}
        <div className="sp-status-pills">
          <button className={`sp-pill${statusFilter === "all" ? " sp-pill-active" : ""}`} onClick={() => setStatusFilter("all")}>All Statuses</button>
          {ALL_STATUSES.map(s => (
            <button key={s} className={`sp-pill sp-pill-${STATUS_CONFIG[s]?.cls}${statusFilter === s ? " sp-pill-active" : ""}`} onClick={() => setStatusFilter(s)}>
              {STATUS_CONFIG[s]?.label || s}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="sp-search-wrap">
          <svg className="sp-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="sp-search"
            placeholder="Search company, contact, product…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="sp-search-clear" onClick={() => setSearch("")}>×</button>}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="sp-grid-wrap">
        <div className="sp-grid-scroll">
          <table className="sp-grid" style={{ minWidth: Object.values(colWidths).reduce((a,b)=>a+b,0), width: "100%" }}>
            <colgroup>
              {COLS.map(({ key }) => {
                const wKey = key ?? "__notes";
                return <col key={wKey} style={{ width: colWidths[wKey] }} />;
              })}
            </colgroup>
            <thead>
              <tr>
                {COLS.map(({ key, label }) => {
                  const wKey = key ?? "__notes";
                  return (
                    <th
                      key={wKey}
                      className={`sp-th${key && sortKey === key ? " sp-th-sorted" : ""}`}
                      onClick={key ? () => handleSort(key) : undefined}
                    >
                      <span className="sp-th-inner" style={key ? { cursor: "pointer" } : {}}>
                        {label}
                        {key && <SortIcon col={key} />}
                      </span>
                      <span
                        className="sp-resize-handle"
                        onMouseDown={e => startResize(e, key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} className="sp-empty">
                    No records match your filters.
                    {approvalFilter !== "all" && (
                      <>
                        {" "}
                        <button type="button" className="sp-empty-clear" onClick={() => setApprovalFilter("all")}>
                          Show all requests
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ) : filtered.map((row, i) => {
                const isExpanded = expandedId === row.id;
                const statusCfg  = STATUS_CONFIG[row.status] || { label: row.status, cls: "status-not-shipped" };
                const tUrl       = trackingUrl(row.carrier, row.tracking);
                const hasNotes   = row.notes && row.notes.length > 2;
                // Migration 0097 provenance + review state.
                const apprBadge  = approvalBadge(row);
                const awaiting   = isAwaitingApproval(row);
                const shipBlocked = isShipBlocked(row);
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`sp-row${isExpanded ? " sp-row-expanded" : ""}${awaiting ? " sp-row-awaiting" : ""}`}
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    >
                      <td className="sp-td sp-td-details" onClick={e => e.stopPropagation()}>
                        <button
                          className={`sp-details-btn${isExpanded ? " sp-details-btn-active" : ""}`}
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        >
                          Details
                        </button>
                      </td>
                      <td className="sp-td sp-td-status" onClick={e => e.stopPropagation()}>
                        {isEdit(row.id, "status")
                          /* `Shipped` is REMOVED from the list for a blocked row.
                             The control that would hit 0097's trigger is not on
                             screen, so the refusal cannot be reached from here. */
                          ? spSelect(row.id, "status", shipStatusOptions(ALL_STATUSES, row))
                          : <span className={`sp-badge ${statusCfg.cls}${canEdit ? " sp-editable" : ""}`} onClick={canEdit ? e => startEdit(row.id, "status", row.status, e) : undefined}>{statusCfg.label}</span>
                        }
                        {apprBadge && (
                          <span
                            className={`sp-appr-badge ${apprBadge.cls}`}
                            title={
                              awaiting
                                ? "Submitted through your public sample form — approve it before it can ship"
                                : isFromEmbed(row) ? "Submitted through your public sample form" : undefined
                            }
                          >
                            {apprBadge.label}
                          </span>
                        )}
                      </td>
                      <td className="sp-td sp-td-company" title={row.address} onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "shipToCompany", <div className="sp-company">{row.shipToCompany || "—"}</div>, row.shipToCompany)}
                      </td>
                      <td className="sp-td sp-td-contact" onClick={e => e.stopPropagation()}>
                        {isEdit(row.id, "contactName")  ? spInput(row.id, "contactName")  : <div className={`sp-contact-name${canEdit ? " sp-editable" : ""}`}  onClick={canEdit ? e => startEdit(row.id, "contactName",  row.contactName,  e) : undefined}>{row.contactName  || "—"}</div>}
                        {isEdit(row.id, "contactPhone") ? spInput(row.id, "contactPhone") : <div className={`sp-contact-phone${canEdit ? " sp-editable" : ""}`} onClick={canEdit ? e => startEdit(row.id, "contactPhone", row.contactPhone, e) : undefined}>{row.contactPhone || (canEdit ? <span className="sp-editable-empty">+ phone</span> : null)}</div>}
                      </td>
                      <td className="sp-td sp-td-state" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "state", row.state || "—", row.state)}
                      </td>
                      <td className="sp-td sp-td-product" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "product", <div className="sp-product" title={row.product}>{row.product || "—"}</div>, row.product)}
                      </td>
                      <td className="sp-td sp-td-date" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "orderDate", fmt(row.orderDate), row.orderDate, "date")}
                      </td>
                      <td className="sp-td sp-td-date" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "requestedShipDate", fmt(row.requestedShipDate), row.requestedShipDate, "date")}
                      </td>
                      <td className="sp-td sp-td-submitter" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "submitter", <div className="sp-submitter">{row.submitter || "—"}</div>, row.submitter)}
                      </td>
                      <td className="sp-td sp-td-ship" onClick={e => e.stopPropagation()}>
                        {spCell(row.id, "shipDate", fmt(row.shipDate), row.shipDate, "date")}
                      </td>
                      <td className="sp-td sp-td-tracking" onClick={e => e.stopPropagation()}>
                        <span className="sp-carrier">{row.carrier || "—"}</span>
                        {row.tracking
                          ? (tUrl
                              ? <a className="sp-tracking-link" href={tUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{row.tracking}</a>
                              : <span className="sp-tracking">{row.tracking}</span>
                            )
                          : null
                        }
                      </td>
                      <td className="sp-td sp-td-notes" onClick={e => e.stopPropagation()}>
                        <div className="sp-row-actions">
                          {hasNotes && (
                            <span className="sp-notes-icon" title={row.notes} onClick={e => { e.stopPropagation(); setExpandedId(isExpanded ? null : row.id); }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                            </span>
                          )}
                          {awaiting && canEdit && (
                            <button
                              className="sp-row-review-btn"
                              onClick={e => { e.stopPropagation(); setReviewRow(row); }}
                              title="Review the submitted address and rule on this request"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
                              </svg>
                              Review
                            </button>
                          )}
                          {canEditShipping && (
                            <button
                              className="sp-row-edit-btn"
                              onClick={e => { e.stopPropagation(); setShippingEditRow(row); }}
                              title={shipBlocked
                                ? "Shipping details — this request still needs approval before it can ship"
                                : "Edit shipping details"}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                              </svg>
                              Edit Shipping
                            </button>
                          )}
                          {canDeleteRequest && (
                            confirmDeleteId === row.id ? (
                              <span className="sp-row-delete-confirm">
                                <button className="sp-del-yes" onClick={e => { e.stopPropagation(); handleDelete(row.id); }} disabled={deleting}>{deleting ? "…" : "Yes"}</button>
                                <button className="sp-del-no"  onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}>No</button>
                              </span>
                            ) : (
                              <button
                                className="sp-row-delete-btn"
                                onClick={e => { e.stopPropagation(); setConfirmDeleteId(row.id); }}
                                title="Delete record"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                  <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                </svg>
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="sp-detail-row">
                        <td colSpan={COLS.length}>
                          <div className="sp-detail">
                            <div className="sp-detail-section">
                              <div className="sp-detail-label">Ship To Address</div>
                              <div className="sp-detail-value">{row.address || "—"}</div>
                            </div>
                            <div className="sp-detail-section">
                              <div className="sp-detail-label">Contact Email</div>
                              <div className="sp-detail-value">{row.contactEmail || "—"}</div>
                            </div>
                            <div className="sp-detail-section">
                              <div className="sp-detail-label">Full Product List</div>
                              <div className="sp-detail-value">{row.product || "—"}</div>
                            </div>
                            {hasNotes && (
                              <div className="sp-detail-section sp-detail-notes">
                                <div className="sp-detail-label">Notes</div>
                                <div className="sp-detail-value">{row.notes}</div>
                              </div>
                            )}
                            {row.estDelivery && (
                              <div className="sp-detail-section">
                                <div className="sp-detail-label">Est. Delivery</div>
                                <div className="sp-detail-value">{fmt(row.estDelivery)}</div>
                              </div>
                            )}
                            <div className="sp-detail-section">
                              <div className="sp-detail-label">Submitter Email</div>
                              <div className="sp-detail-value">{row.submitterEmail || "—"}</div>
                            </div>
                            {isFromEmbed(row) && (
                              <div className="sp-detail-section">
                                <div className="sp-detail-label">Source</div>
                                <div className="sp-detail-value">
                                  Public sample form
                                  {approvalStatusOf(row) === APPROVAL.APPROVED && row.approvedAt
                                    ? ` — approved ${fmt(String(row.approvedAt).slice(0, 10))}`
                                    : ""}
                                </div>
                              </div>
                            )}
                            {approvalStatusOf(row) === APPROVAL.REJECTED && (
                              <div className="sp-detail-section sp-detail-notes">
                                <div className="sp-detail-label">Rejected because</div>
                                <div className="sp-detail-value">{row.rejectedReason || "—"}</div>
                              </div>
                            )}
                            {awaiting && (
                              <div className="sp-detail-section sp-detail-notes sp-detail-awaiting">
                                <div className="sp-detail-label">Waiting on you</div>
                                <div className="sp-detail-value">
                                  This request came in through your public sample form, so the ship-to
                                  address above was typed by the requester. It cannot be marked Shipped
                                  until someone approves it.
                                  {canEdit ? "" : " Ask a workspace owner or admin to review it."}
                                </div>
                              </div>
                            )}
                            {(canEditShipping || canDeleteRequest || (awaiting && canEdit)) && (
                              <div className="sp-detail-section sp-detail-actions">
                                {awaiting && canEdit && (
                                  <button
                                    className="sp-review-btn"
                                    onClick={e => { e.stopPropagation(); setReviewRow(row); }}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
                                    </svg>
                                    Review approval
                                  </button>
                                )}
                                {canEditShipping && (
                                  <button
                                    className="sp-edit-shipping-btn"
                                    onClick={e => { e.stopPropagation(); setShippingEditRow(row); }}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                                    </svg>
                                    Edit Shipping Details
                                  </button>
                                )}
                                {canDeleteRequest && (
                                  confirmDeleteId === row.id ? (
                                    <span className="sp-delete-confirm">
                                      <span className="sp-delete-confirm-text">Permanently delete this record?</span>
                                      <button
                                        className="sp-delete-confirm-btn"
                                        onClick={e => { e.stopPropagation(); handleDelete(row.id); }}
                                        disabled={deleting}
                                      >
                                        {deleting ? "Deleting…" : "Yes, Delete"}
                                      </button>
                                      <button
                                        className="sp-delete-cancel-btn"
                                        onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                        disabled={deleting}
                                      >
                                        Cancel
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      className="sp-delete-btn"
                                      onClick={e => { e.stopPropagation(); setConfirmDeleteId(row.id); }}
                                      title="Delete this record"
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/>
                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                        <path d="M10 11v6M14 11v6"/>
                                        <path d="M9 6V4h6v2"/>
                                      </svg>
                                      Delete Record
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="sp-grid-footer">
          Showing {filtered.length} of {sampleRequests.length} records
        </div>
      </div>
    </div>

    {successToast && (
      <div className="sp-success-toast">
        Thanks for submitting your order!
      </div>
    )}

    {notifyWarning && (
      <div className="sp-notify-warning" role="alert">
        <span>{notifyWarning}</span>
        <button type="button" onClick={() => setNotifyWarning(null)} aria-label="Dismiss">×</button>
      </div>
    )}

    {shippingEditRow && (
      <ShippingEditModal
        request={shippingEditRow}
        user={user}
        onNotifyResult={showNotifyWarning}
        // The modal drops its own Shipped option and its Create-label button
        // when this is set, so the shipping surface cannot reach 0097's trigger.
        approvalBlocked={isShipBlocked(shippingEditRow)}
        onReview={
          isAwaitingApproval(shippingEditRow) && canEdit
            ? () => { const r = shippingEditRow; setShippingEditRow(null); setReviewRow(r); }
            : undefined
        }
        onClose={() => setShippingEditRow(null)}
        onSaved={(updated) => {
          setSampleRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
          setShippingEditRow(null);
        }}
      />
    )}

    {reviewRow && (
      <SampleApprovalModal
        request={reviewRow}
        onClose={() => setReviewRow(null)}
        onRuled={handleRuled}
      />
    )}

    {showNewModal && (
      <NewRequestModal
        user={user}
        onClose={() => setShowNewModal(false)}
        onCreated={(newRecord) => {
          setShowNewModal(false);
          setSuccessToast(true);
          setTimeout(() => setSuccessToast(false), 4000);
          // Normalize the DB record and prepend to the list
          setSampleRequests(prev => [{
            id:                newRecord.id,
            submitter:         newRecord.submitter_name      ?? "",
            shipToCompany:     newRecord.ship_to_company     ?? "",
            address:           newRecord.address             ?? "",
            state:             newRecord.state               ?? "",
            contactName:       newRecord.contact_name        ?? "",
            contactPhone:      newRecord.contact_phone       ?? "",
            contactEmail:      newRecord.contact_email       ?? "",
            orderDate:         newRecord.order_date          ?? "",
            requestedShipDate: newRecord.requested_ship_date ?? "",
            product:           newRecord.product             ?? "",
            quantity:          newRecord.quantity            ?? "",
            notes:             newRecord.notes               ?? "",
            submitterEmail:    newRecord.submitter_email     ?? "",
            status:            newRecord.status              ?? "Not Shipped",
            shipDate:          newRecord.ship_date           ?? "",
            estDelivery:       newRecord.est_delivery        ?? "",
            carrier:           newRecord.carrier             ?? "",
            tracking:          newRecord.tracking            ?? "",
            section:           "new",
            // A rep typed this one. It is `app` / `not_required` and behaves
            // exactly as it always has — no approval gate in front of it.
            source:            newRecord.source              ?? "app",
            approvalStatus:    newRecord.approval_status     ?? APPROVAL.NOT_REQUIRED,
            approvedAt:        newRecord.approved_at         ?? "",
            rejectedReason:    newRecord.rejected_reason     ?? "",
            embedKeyId:        newRecord.embed_key_id        ?? null,
          }, ...prev]);
        }}
      />
    )}
    </>
  );
}
